/* ============================================================
   LightOrb — a sphere of light: a thin glowing rim with silk-like
   veils of light folding slowly around and through it.

   How the look is made
     · Every surface is treated as a thin sheet that gives off light. Seen
       face-on it is faint; seen edge-on (the rim of the ball, a fold in a
       veil) the light piles up and burns out to pink-white.
     · Everything is drawn in high dynamic range, blurred into a soft glow,
       then compressed to the screen, which is what turns deep violet into
       magenta and then white as it gets brighter.

   WebGL 2, written directly — no three.js, no libraries, nothing loaded
   from another domain.

   The canvas is transparent: only the light is drawn, and it fades to
   nothing before the canvas ends. Because it is light, it needs a dark
   background behind it to be seen.

   USE
     1. Load this file once on the page (a normal deferred script tag).
     2. Put a canvas with the attribute  data-light-orb  inside a box that
        has a size. Give the canvas  display:block; width:100%; height:100%.

     Options as attributes:  data-speed="0.6"  data-size="0.34"  data-glow="1.2"
     or in code:
       var orb = LightOrb(canvasElement, { speed: 0.6 });
       orb.pause(); orb.play(); orb.destroy();
   ============================================================ */
(function (global) {
  'use strict';

  var DEFAULTS = {
    size: 0.28,          // radius of the orb, as a fraction of the canvas's shorter side (leave room for the glow)
    speed: 1,            // how fast the veils flow (1 = slow and calm · 0 = frozen)
    spin: 1,             // how fast the whole orb turns (1 = one turn in about 90 seconds · 0 = no turning)
    glow: 1,             // strength of the soft glow around the light
    brightness: 1,       // overall exposure
    rim: 1,              // brightness of the sphere's outline (0 = veils only)
    energy: 0,           // 0 = calm · 1 = fully awake: more white light and a stronger glow. Meant to be driven from code: orb.set('energy', 0.6)
    interactive: 1,      // 1 = tilts slightly towards the pointer · 0 = off
    quality: 1,          // 1 = full resolution · 0.5 = half (lighter on weak devices)
    seed: 0              // any number: a different arrangement of veils
  };

  /* Colours measured from the reference image. They are light, not paint:
     the brighter a spot gets, the further it runs from these towards white. */
  var COLORS = {
    indigo:  [0.16, 0.00, 0.95],
    violet:  [0.30, 0.01, 1.00],
    purple:  [0.46, 0.02, 1.00],
    magenta: [1.00, 0.10, 0.86]
  };

  /* The veils. Each is a closed band wrapped around the ball along a seam-like
     path (think of the seam of a tennis ball), twisting and changing width. */
  var VEILS = [
    { tilt: [0.55, 0.40, -0.35], amp: 0.62, width: 0.74, twist: 0.16, twists: 2, flow: 0.022, phase: 0.6, radius: 0.99, lift: 0.03,
      gain: 0.330, body: 0.32, fibres: 12, fibre: 9.0, a: 'violet', b: 'purple' },
    { tilt: [-0.80, 1.90, 0.50], amp: 0.48, width: 0.36, twist: 0.28, twists: 3, flow: -0.016, phase: 2.4, radius: 0.985, lift: 0.10,
      gain: 0.060, body: 0.3, fibres: 12, fibre: 9.0, a: 'indigo', b: 'violet' },
    { tilt: [1.60, -0.70, 1.10], amp: 0.80, width: 0.50, twist: 1.50, twists: 2, flow: 0.012, phase: 4.3, radius: 0.94, lift: 0.30,
      gain: 0.006, body: 0.15, fibres: 24, fibre: 1.6, a: 'indigo', b: 'violet' }
  ];

  var VS_VEIL = [
    '#version 300 es',
    'precision highp float;',
    'layout(location=0) in vec2 aTW;',            // t along the band 0…1, w across it -1…1
    'uniform float uTime;',
    'uniform mat3 uRot;',
    'uniform vec2 uScale;',
    'uniform vec4 uShape;',                        // seam amplitude, width, twist amount, twists per lap
    'uniform vec4 uMove;',                         // flow, phase, radius, lift
    'out vec3 vN; out vec2 vTW; out vec3 vP; out float vDense; out float vWide;',
    'const float TAU = 6.28318530718;',
    'vec3 seam(float a){ return normalize(vec3(cos(a), uShape.x * sin(2.0 * a), sin(a))); }',
    'float widthAt(float f){ return uShape.y * (0.03 + pow(0.5 + 0.5 * sin(f + 0.8 * sin(2.0 * f + uMove.y)), 2.2)); }',   // wide for a stretch, a thread for the rest
    'vec3 surf(float t, float w){',
    '  float a = t * TAU;',
    '  vec3 P = seam(a);',
    '  vec3 T = normalize(seam(a + 0.01) - seam(a - 0.01));',
    '  vec3 B = cross(P, T);',
    '  float f = a - uTime * uMove.x * TAU + uMove.y;',
    '  float tw = uShape.z * sin(f * uShape.w + 1.3) + 0.35 * sin(f * 1.0 - 0.7);',
    '  tw += w * 0.35;',                                                   // the band is cupped, so its edges turn away and catch the light
    '  vec3 D = cos(tw) * B - sin(tw) * P;',                              // and it tilts into the ball as it twists
    '  float r = uMove.z * (1.0 - uMove.w * (0.5 + 0.5 * sin(f * 2.0 + 2.1)));',
    '  vec3 S = P * r + D * (w * widthAt(f));',
    '  return S * (0.99 / max(length(S), 0.99));',                        // never leaves the ball: it presses against the inside
    '}',
    'void main(){',
    '  float t = aTW.x, w = aTW.y, e = 0.0015;',
    '  vec3 S = surf(t, w);',
    '  vec3 N = normalize(cross(surf(t + e, w) - surf(t - e, w), surf(t, w + 0.02) - surf(t, w - 0.02)));',
    '  vec3 p = uRot * S;',
    '  vN = uRot * N; vTW = aTW; vP = p;',
    '  float f = t * TAU - uTime * uMove.x * TAU + uMove.y;',
    '  float rel = widthAt(f) / uShape.y;',
    '  vDense = pow(1.0 / max(rel, 0.05), 0.45);',                        // where the band narrows, the same light is packed tighter
    '  vWide = smoothstep(0.04, 0.3, rel);',
    '  gl_Position = vec4(p.xy * uScale, 0.0, 1.0);',
    '}'
  ].join('\n');

  var FS_VEIL = [
    '#version 300 es',
    'precision highp float;',
    'in vec3 vN; in vec2 vTW; in vec3 vP; in float vDense; in float vWide;',
    'uniform vec3 uC1, uC2, uCold, uWarm;',
    'uniform float uHot, uBody;',
    'uniform vec4 uLook;',                         // gain, strand frequency across the band, strand sharpness, phase
    'uniform float uTime;',
    'out vec4 o;',
    'void main(){',
    '  float facing = abs(normalize(vN).z);',
    '  float fold = min(0.15 * pow(1.0 / (facing + 0.035), 1.8), 28.0);',   // edge-on = bright, face-on = faint; capped so a sheet seen exactly edge-on stays a smooth line
    '  fold = mix(min(fold, 4.0), fold, vWide);',                         // a thread is just a line of light, with no folds to flare
    '  float w = vTW.y;',
    '  float soft = smoothstep(1.0, 0.7, abs(w));',                       // the band has no hard edge
    '  float drift = 1.8 * sin(vTW.x * 12.566 + uLook.w);',
    '  float ridge = 0.62 * pow(0.5 + 0.5 * sin(w * uLook.y + drift), uLook.z)',
    '              + 0.38 * pow(0.5 + 0.5 * sin(w * uLook.y * 0.43 - drift * 0.6 + 1.7), uLook.z * 0.5);',   // strands gather in bundles
    '  float fib = mix(0.22, 1.25, ridge);',                               // the band is made of soft parallel strands of light that drift across it
    '  float depth = mix(0.42, 1.0, smoothstep(-0.9, 0.9, vP.z));',       // the far side is dimmer
    '  float lit = smoothstep(-1.0, 1.0, dot(vP.xy, vec2(0.98, 0.2)));',   // the light sits to the right: that side is brighter and warmer
    '  float I = uLook.x * soft * fib * (uBody + fold) * depth * vDense * mix(0.12, 2.0, pow(lit, 1.5));',   // a soft glow through the body of the veil, and bright edges where it folds
    '  float wave = 0.5 + 0.5 * sin(vTW.x * 6.2832 + w * 1.4 + uLook.w + uTime * 0.03);',
    '  float bandPx = 2.0 / max(fwidth(w), 1e-4);',                      // how wide the band is on screen, in pixels
    '  I *= mix(0.2, 1.0, smoothstep(1.5, 7.0, bandPx));',              // a sheet seen exactly edge-on is thinner than a pixel: keep it soft, so it never breaks into sparkling dots
    '  float heat = max(smoothstep(0.8, 13.0, fold), smoothstep(0.2, 1.9, I));',   // where the light is strongest, it runs hot
    '  vec3 c = mix(uCold, mix(uC1, uC2, wave), clamp(0.05 + 0.7 * heat + 0.3 * lit, 0.0, 1.0));',   // faint light is indigo-blue, strong light violet
    '  c = mix(c, uWarm, 0.35 * heat * wave * lit);',                    // the brightest folds on the lit side catch magenta
    '  c = mix(c, vec3(1.0, 0.80, 1.0), uHot * heat);',                    // the strongest folds run pink-white
    '  o = vec4(c * I, 1.0);',
    '}'
  ].join('\n');

  var VS_QUAD = [
    '#version 300 es',
    'layout(location=0) in vec2 aPos;',
    'out vec2 vUv; out vec2 vP;',
    'void main(){ vUv = aPos * 0.5 + 0.5; vP = aPos; gl_Position = vec4(aPos, 0.0, 1.0); }'
  ].join('\n');

  /* the ball itself: a thin shell of light, so only its outline shows */
  var FS_SHELL = [
    '#version 300 es',
    'precision highp float;',
    'in vec2 vP;',
    'uniform vec2 uInv;',                          // clip units → orb radii
    'uniform float uPx, uGain, uTime, uHot;',
    'uniform mat3 uSpinT;',
    'uniform vec3 uCa, uCb, uCc;',
    'out vec4 o;',
    'void main(){',
    '  vec2 q = vP * uInv; float r2 = dot(q, q), r = sqrt(r2);',
    '  float inside = smoothstep(1.0 + uPx, 1.0 - uPx, r);',
    '  float rim = 0.0032 / (max(1.0 - r2, 0.0) + 0.0032) * smoothstep(0.6, 0.97, r);',   // only the outline: nothing spills into the dark inside
    '  vec3 m = uSpinT * vec3(q / max(r, 1e-4), 0.0);',                   // which part of the ball is on the outline here
    '  float k = 0.5 + 0.5 * sin(m.x * 2.3 + m.y * 1.7 + 1.0 + uTime * 0.05);',
    '  float k2 = 0.5 + 0.5 * sin(m.z * 2.9 - m.x * 1.3 + 2.0 - uTime * 0.03);',
    '  float lit = smoothstep(-1.0, 1.0, dot(q / max(r, 1e-4), vec2(0.98, 0.2)));',
    '  float bright = (0.06 + 0.94 * k * k * k) * mix(0.3, 1.45, lit * lit);',
    '  vec3 c = mix(uCa, mix(uCb, uCc, k2 * 0.6), clamp(bright * 1.1 + 0.25 * lit, 0.0, 1.0));',
    '  c = mix(c, vec3(1.0, 0.80, 1.0), uHot * clamp(bright, 0.0, 1.0) * 0.8);',
    '  vec3 col = c * rim * bright * uGain * inside;',
    '  o = vec4(col, 1.0);',
    '}'
  ].join('\n');

  var FS_DOWN = [
    '#version 300 es',
    'precision highp float;',
    'in vec2 vUv; uniform sampler2D uTex; uniform vec2 uTexel; uniform float uKnee; out vec4 o;',
    'void main(){',
    '  vec3 c = texture(uTex, vUv).rgb * 0.5;',
    '  c += (texture(uTex, vUv + uTexel * vec2( 1.0,  1.0)).rgb + texture(uTex, vUv + uTexel * vec2(-1.0,  1.0)).rgb',
    '      + texture(uTex, vUv + uTexel * vec2( 1.0, -1.0)).rgb + texture(uTex, vUv + uTexel * vec2(-1.0, -1.0)).rgb) * 0.125;',
    '  float l = max(c.r, max(c.g, c.b));',
    '  o = vec4(c * smoothstep(0.0, uKnee, l), 1.0);',                    // first step only: faint light does not glow
    '}'
  ].join('\n');

  var FS_UP = [
    '#version 300 es',
    'precision highp float;',
    'in vec2 vUv; uniform sampler2D uTex; uniform vec2 uTexel; uniform float uWeight; out vec4 o;',
    'void main(){',
    '  vec3 c = texture(uTex, vUv).rgb * 4.0;',
    '  c += (texture(uTex, vUv + uTexel * vec2(1.0, 0.0)).rgb + texture(uTex, vUv - uTexel * vec2(1.0, 0.0)).rgb',
    '      + texture(uTex, vUv + uTexel * vec2(0.0, 1.0)).rgb + texture(uTex, vUv - uTexel * vec2(0.0, 1.0)).rgb) * 2.0;',
    '  c += texture(uTex, vUv + uTexel).rgb + texture(uTex, vUv - uTexel).rgb',
    '     + texture(uTex, vUv + uTexel * vec2(1.0, -1.0)).rgb + texture(uTex, vUv + uTexel * vec2(-1.0, 1.0)).rgb;',
    '  o = vec4(c / 16.0 * uWeight, 1.0);',
    '}'
  ].join('\n');

  var FS_FINAL = [
    '#version 300 es',
    'precision highp float;',
    'in vec2 vUv; uniform sampler2D uScene, uBloom; uniform vec2 uTexel; uniform float uGlow, uExposure, uFade; out vec4 o;',
    'vec3 soft(vec2 p){',                                                 // the light, very slightly softened: thin lines stay smooth instead of breaking into dashes
    '  vec3 c = texture(uScene, p).rgb * 0.25;',
    '  c += (texture(uScene, p + vec2(uTexel.x, 0.0)).rgb + texture(uScene, p - vec2(uTexel.x, 0.0)).rgb',
    '      + texture(uScene, p + vec2(0.0, uTexel.y)).rgb + texture(uScene, p - vec2(0.0, uTexel.y)).rgb) * 0.125;',
    '  c += (texture(uScene, p + uTexel).rgb + texture(uScene, p - uTexel).rgb',
    '      + texture(uScene, p + vec2(uTexel.x, -uTexel.y)).rgb + texture(uScene, p + vec2(-uTexel.x, uTexel.y)).rgb) * 0.0625;',
    '  return c;',
    '}',
    'void main(){',
    '  vec3 c = soft(vUv) + texture(uBloom, vUv).rgb * uGlow;',
    '  c = (1.0 - exp(-c * uExposure)) * uFade;',                         // bright light runs to white, as on film
    '  vec2 edge = smoothstep(0.0, 0.05, vUv) * smoothstep(0.0, 0.05, 1.0 - vUv);',
    '  c *= edge.x * edge.y;',                                            // the glow always dies away before the canvas ends, so no box ever shows
    '  o = vec4(c, max(c.r, max(c.g, c.b)));',
    '}'
  ].join('\n');

  function rotX(a) { var c = Math.cos(a), s = Math.sin(a); return [1, 0, 0, 0, c, -s, 0, s, c]; }
  function rotY(a) { var c = Math.cos(a), s = Math.sin(a); return [c, 0, s, 0, 1, 0, -s, 0, c]; }
  function rotZ(a) { var c = Math.cos(a), s = Math.sin(a); return [c, -s, 0, s, c, 0, 0, 0, 1]; }
  function mul(a, b) {                              // row-major 3×3
    var o = new Array(9), i, j;
    for (i = 0; i < 3; i++) for (j = 0; j < 3; j++) o[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j];
    return o;
  }
  function cols(m) { return new Float32Array([m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]]); }   // for WebGL (column-major)
  function transpose(m) { return [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]]; }

  function LightOrb(canvas, options) {
    var cfg = {}, key;
    for (key in DEFAULTS) cfg[key] = options && options[key] !== undefined ? options[key] : DEFAULTS[key];
    var noop = { play: function () {}, pause: function () {}, advance: function () {}, set: function () {}, destroy: function () {} };

    var gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false });
    if (!gl) {
      if (global.console) console.warn('LightOrb: WebGL 2 is not available, nothing drawn.');
      return noop;
    }

    var hdr = false, progs = {}, quad, veilBuf, veilCount = 0, scene = null, smooth = null, levels = [], LEVELS = 6, lost = false;
    var T_SEG = 1000, W_SEG = 18;

    function shader(type, src) {
      var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }
    function program(vs, fs, names) {
      var p = gl.createProgram();
      gl.attachShader(p, shader(gl.VERTEX_SHADER, vs)); gl.attachShader(p, shader(gl.FRAGMENT_SHADER, fs));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
      var u = {}; names.forEach(function (n) { u[n] = gl.getUniformLocation(p, n); });
      return { p: p, u: u };
    }
    function target(w, h) {
      var tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, tex);
      if (hdr) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, w, h, 0, gl.RGBA, gl.HALF_FLOAT, null);
      else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      var fb = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      return { tex: tex, fb: fb, w: w, h: h };
    }
    function drop(t) { if (t) { gl.deleteTexture(t.tex); gl.deleteFramebuffer(t.fb); } }
    /* a multisampled surface to draw into, so edge-on sheets stay smooth lines instead of breaking into dots */
    function smoothTarget(w, h) {
      var samples = Math.min(4, gl.getParameter(gl.MAX_SAMPLES) || 0);
      if (samples < 2) return null;
      var rb = gl.createRenderbuffer(); gl.bindRenderbuffer(gl.RENDERBUFFER, rb);
      gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, hdr ? gl.RGBA16F : gl.RGBA8, w, h);
      var fb = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, rb);
      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) { gl.deleteRenderbuffer(rb); gl.deleteFramebuffer(fb); return null; }
      return { rb: rb, fb: fb };
    }
    function dropSmooth(t) { if (t) { gl.deleteRenderbuffer(t.rb); gl.deleteFramebuffer(t.fb); } }

    function setup() {
      hdr = !!(gl.getExtension('EXT_color_buffer_float') || gl.getExtension('EXT_color_buffer_half_float'));
      progs.veil = program(VS_VEIL, FS_VEIL, ['uTime', 'uRot', 'uScale', 'uShape', 'uMove', 'uC1', 'uC2', 'uLook', 'uCold', 'uWarm', 'uHot', 'uBody']);
      progs.shell = program(VS_QUAD, FS_SHELL, ['uInv', 'uPx', 'uGain', 'uTime', 'uSpinT', 'uCa', 'uCb', 'uCc', 'uHot']);
      progs.down = program(VS_QUAD, FS_DOWN, ['uTex', 'uTexel', 'uKnee']);
      progs.up = program(VS_QUAD, FS_UP, ['uTex', 'uTexel', 'uWeight']);
      progs.fin = program(VS_QUAD, FS_FINAL, ['uScene', 'uBloom', 'uTexel', 'uGlow', 'uExposure', 'uFade']);

      quad = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

      /* one band of triangles, reused for every veil */
      var v = new Float32Array(T_SEG * W_SEG * 12), n = 0, i, j;
      for (i = 0; i < T_SEG; i++) for (j = 0; j < W_SEG; j++) {
        var t0 = i / T_SEG, t1 = (i + 1) / T_SEG, w0 = j / W_SEG * 2 - 1, w1 = (j + 1) / W_SEG * 2 - 1;
        v[n++] = t0; v[n++] = w0; v[n++] = t1; v[n++] = w0; v[n++] = t1; v[n++] = w1;
        v[n++] = t0; v[n++] = w0; v[n++] = t1; v[n++] = w1; v[n++] = t0; v[n++] = w1;
      }
      veilCount = n / 2;
      veilBuf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, veilBuf); gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.disable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE);
      scene = null; smooth = null; levels = [];
    }

    /* ---- size ---- */
    var W = 1, H = 1, PW = 1, PH = 1;
    function resize() {
      var box = canvas.getBoundingClientRect();
      W = Math.max(1, box.width); H = Math.max(1, box.height);
      var dpr = Math.min(global.devicePixelRatio || 1, 2) * Math.max(0.25, Math.min(1, cfg.quality));
      var px = W * H * dpr * dpr, cap = 1.6e6;                       // a large canvas is drawn a little smaller and scaled up: the light is soft, and it keeps memory low
      if (px > cap) dpr *= Math.sqrt(cap / px);
      PW = Math.max(2, Math.round(W * dpr)); PH = Math.max(2, Math.round(H * dpr));
      canvas.width = PW; canvas.height = PH;
      drop(scene); dropSmooth(smooth); levels.forEach(drop);
      scene = target(PW, PH); smooth = smoothTarget(PW, PH); levels = [];
      var w = PW, h = PH, i;
      for (i = 0; i < LEVELS; i++) { w = Math.max(2, w >> 1); h = Math.max(2, h >> 1); levels.push(target(w, h)); }
    }

    /* ---- pointer ---- */
    var still = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var aimX = 0, aimY = 0, tiltX = 0, tiltY = 0;
    function onMove(e) {
      var box = canvas.getBoundingClientRect();
      aimX = Math.max(-1, Math.min(1, (e.clientX - (box.left + box.width / 2)) / (global.innerWidth / 2)));
      aimY = Math.max(-1, Math.min(1, (e.clientY - (box.top + box.height / 2)) / (global.innerHeight / 2)));
    }
    var hover = cfg.interactive > 0 && !still && global.matchMedia && global.matchMedia('(hover: hover)').matches;
    if (hover) global.addEventListener('pointermove', onMove, { passive: true });

    function fullscreen() { gl.bindBuffer(gl.ARRAY_BUFFER, quad); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0); gl.drawArrays(gl.TRIANGLES, 0, 3); }

    /* ---- one frame ---- */
    var flowT = cfg.seed * 37.7 + 40, spinT = cfg.seed * 11.3, fade = 0;
    function draw() {
      if (lost || !scene) return;
      var short = Math.min(PW, PH), Rpx = cfg.size * short;
      var sx = Rpx / (PW / 2), sy = Rpx / (PH / 2), k = hdr ? 1 : 0.3;      // without HDR buffers, draw darker and expose more
      var en = Math.max(0, Math.min(1.5, cfg.energy)), hot = Math.min(0.6, 0.16 + 0.34 * en);   // how awake it is, and how white its brightest light runs
      tiltX += (aimX - tiltX) * 0.04; tiltY += (aimY - tiltY) * 0.04;
      var spin = mul(rotX(0.30 + 0.12 * Math.sin(spinT * 0.13) + tiltY * 0.22), rotY(spinT * 0.07 + tiltX * 0.3));

      /* 1 · the light itself, added up in high dynamic range */
      gl.bindFramebuffer(gl.FRAMEBUFFER, smooth ? smooth.fb : scene.fb); gl.viewport(0, 0, PW, PH);
      gl.clearColor(0, 0, 0, 1); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE);

      if (cfg.rim > 0) {
        var s = progs.shell; gl.useProgram(s.p);
        gl.uniform2f(s.u.uInv, 1 / sx, 1 / sy); gl.uniform1f(s.u.uPx, 1.2 / Rpx);
        gl.uniform1f(s.u.uGain, 8.5 * cfg.rim * k * (1 + 0.25 * en)); gl.uniform1f(s.u.uTime, flowT); gl.uniform1f(s.u.uHot, hot);
        gl.uniformMatrix3fv(s.u.uSpinT, false, cols(transpose(spin)));
        gl.uniform3fv(s.u.uCa, COLORS.indigo); gl.uniform3fv(s.u.uCb, COLORS.violet); gl.uniform3fv(s.u.uCc, COLORS.purple);
        fullscreen();
      }

      var p = progs.veil; gl.useProgram(p.p);
      gl.bindBuffer(gl.ARRAY_BUFFER, veilBuf); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.uniform1f(p.u.uTime, flowT); gl.uniform2f(p.u.uScale, sx, sy);
      gl.uniform3fv(p.u.uCold, COLORS.indigo); gl.uniform3fv(p.u.uWarm, COLORS.magenta); gl.uniform1f(p.u.uHot, hot);
      VEILS.forEach(function (v) {
        var rot = mul(spin, mul(rotZ(v.tilt[2]), mul(rotX(v.tilt[0]), rotY(v.tilt[1]))));
        gl.uniformMatrix3fv(p.u.uRot, false, cols(rot));
        gl.uniform4f(p.u.uShape, v.amp, v.width, v.twist, v.twists);
        gl.uniform4f(p.u.uMove, v.flow, v.phase + cfg.seed * 1.7, v.radius, v.lift);
        gl.uniform3fv(p.u.uC1, COLORS[v.a]); gl.uniform3fv(p.u.uC2, COLORS[v.b]);
        gl.uniform1f(p.u.uBody, v.body);
        gl.uniform4f(p.u.uLook, v.gain * k * (1 + 0.3 * en), v.fibres, v.fibre, v.phase);
        gl.drawArrays(gl.TRIANGLES, 0, veilCount);
      });

      gl.disable(gl.BLEND);
      if (smooth) {
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, smooth.fb); gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, scene.fb);
        gl.blitFramebuffer(0, 0, PW, PH, 0, 0, PW, PH, gl.COLOR_BUFFER_BIT, gl.NEAREST);
      }

      /* 2 · the glow: the picture shrunk step by step, then built back up, each step softer and wider */
      var d = progs.down, src = scene, i; gl.useProgram(d.p); gl.uniform1i(d.u.uTex, 0); gl.activeTexture(gl.TEXTURE0);
      for (i = 0; i < LEVELS; i++) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, levels[i].fb); gl.viewport(0, 0, levels[i].w, levels[i].h);
        gl.bindTexture(gl.TEXTURE_2D, src.tex); gl.uniform2f(d.u.uTexel, 1 / src.w, 1 / src.h);
        gl.uniform1f(d.u.uKnee, i === 0 ? 0.6 * k : 1e-6);
        fullscreen(); src = levels[i];
      }
      var u = progs.up; gl.useProgram(u.p); gl.uniform1i(u.u.uTex, 0);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE);
      for (i = LEVELS - 1; i > 0; i--) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, levels[i - 1].fb); gl.viewport(0, 0, levels[i - 1].w, levels[i - 1].h);
        gl.bindTexture(gl.TEXTURE_2D, levels[i].tex); gl.uniform2f(u.u.uTexel, 1 / levels[i].w, 1 / levels[i].h);
        gl.uniform1f(u.u.uWeight, 0.93);                               // the wide steps of the glow carry almost as far as the tight ones: a broad, soft halo
        fullscreen();
      }

      /* 3 · onto the page */
      gl.disable(gl.BLEND);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, PW, PH);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      var f = progs.fin; gl.useProgram(f.p);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, scene.tex); gl.uniform1i(f.u.uScene, 0); gl.uniform2f(f.u.uTexel, 1 / PW, 1 / PH);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, levels[0].tex); gl.uniform1i(f.u.uBloom, 1);
      gl.uniform1f(f.u.uGlow, 0.3 * cfg.glow * (1 + 0.45 * en)); gl.uniform1f(f.u.uExposure, 6.2 * cfg.brightness * (1 + 0.1 * en) / k); gl.uniform1f(f.u.uFade, fade);
      fullscreen();
      gl.activeTexture(gl.TEXTURE0);
    }

    /* ---- running ---- */
    var raf = 0, playing = true, visible = true, last = 0, dead = false;
    function step(dt) { flowT += dt * cfg.speed; spinT += dt * cfg.spin; if (fade < 1) fade = Math.min(1, fade + dt / 1.6); }
    function tick(now) {
      raf = 0;
      if (!playing || !visible || lost || dead) return;
      var dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016; last = now;
      if (still) { fade = 1; draw(); return; }
      step(dt); draw();
      raf = requestAnimationFrame(tick);
    }
    function wake() { if (!raf && playing && visible && !lost && !dead) { last = 0; raf = requestAnimationFrame(tick); } }

    function onLost(e) { e.preventDefault(); lost = true; }
    function onBack() { lost = false; setup(); resize(); draw(); wake(); }
    canvas.addEventListener('webglcontextlost', onLost);
    canvas.addEventListener('webglcontextrestored', onBack);

    var ro = global.ResizeObserver ? new ResizeObserver(function () { if (!lost) { resize(); draw(); } }) : null;
    if (ro) ro.observe(canvas);
    var io = global.IntersectionObserver ? new IntersectionObserver(function (e) {
      visible = e[e.length - 1].isIntersecting; wake();              // no work while off screen
    }) : null;
    if (io) io.observe(canvas);

    try { setup(); } catch (err) { if (global.console) console.error('LightOrb:', err); return noop; }
    resize(); if (still) fade = 1;
    draw(); wake();

    return {
      play: function () { if (!playing) { playing = true; wake(); } },
      pause: function () { playing = false; },
      /* for testing and stills: move time on by hand and draw one frame */
      advance: function (seconds) { fade = 1; flowT += seconds * cfg.speed; spinT += seconds * cfg.spin; draw(); },
      /* change an option while it runs, e.g. set('glow', 1.4) — not quality or seed */
      set: function (name, value) { if (name in DEFAULTS) { cfg[name] = value; if (!playing || still) draw(); } },
      destroy: function () {
        dead = true; if (raf) cancelAnimationFrame(raf);
        if (ro) ro.disconnect(); if (io) io.disconnect();
        if (hover) global.removeEventListener('pointermove', onMove);
        canvas.removeEventListener('webglcontextlost', onLost);
        canvas.removeEventListener('webglcontextrestored', onBack);
        drop(scene); dropSmooth(smooth); levels.forEach(drop);
      }
    };
  }

  /* start every canvas marked data-light-orb */
  function auto(root) {
    root = root && root.querySelectorAll ? root : document;
    var list = root.querySelectorAll('canvas[data-light-orb]');
    for (var i = 0; i < list.length; i++) {
      var el = list[i], opts = {}, key;
      if (el.__orb) continue;
      for (key in DEFAULTS) {
        var raw = el.dataset[key];
        if (raw === undefined) continue;
        opts[key] = parseFloat(raw);
      }
      el.__orb = LightOrb(el, opts);
    }
  }

  global.LightOrb = LightOrb;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', auto);
  else auto();
  document.addEventListener('shopify:section:load', function (e) { auto(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('canvas[data-light-orb]').forEach(function (el) {
      if (el.__orb) { el.__orb.destroy(); delete el.__orb; }
    });
  });
})(window);
